<script lang="ts">
  import { onMount, type Snippet } from 'svelte';
  import { prefersLandscapePlayback, watchPresentation } from '../player/presentation';
  import { observeWatchGeometry, readWatchGeometry } from './watch-viewport';

  const { children }: { children: Snippet } = $props();
  let root: HTMLDivElement;
  // Synchronous route-entry geometry: loading artwork never waits for media imports.
  let geometry = $state(readWatchGeometry(prefersLandscapePlayback()));
  const css = $derived(`--watch-width:${geometry.logicalWidth}px;--watch-height:${geometry.logicalHeight}px;` +
    `--watch-physical-width:${geometry.width}px;--watch-physical-height:${geometry.height}px;` +
    `--watch-left:${geometry.left}px;--watch-top:${geometry.top}px;`);

  onMount(() => {
    const presentation = watchPresentation(root)!;
    const stop = observeWatchGeometry(prefersLandscapePlayback, value => {
      geometry = value;
    });
    // No gesture is fabricated or held across media/network awaits. When the
    // navigation's activation survives mounting, spend it on the persistent root.
    if (prefersLandscapePlayback() && navigator.userActivation?.isActive) {
      void presentation.enter(false);
    }
    return () => {
      stop();
      presentation.destroy();
    };
  });

  $effect(() => {
    void geometry;
    // Post-render notification for geometry consumers, including unchanged-area
    // quarter-turns which a ResizeObserver alone can miss.
    window.dispatchEvent(new Event('watchviewportchange'));
  });
</script>

<div class="watch-viewport" data-watch-viewport bind:this={root} style={css}>
  <div class="watch-space" data-watch-space data-sideways={String(geometry.sideways)}>
    {@render children()}
  </div>
</div>

<style>
  .watch-viewport {
    position: fixed;
    inset: 0;
    overflow: hidden;
    background: var(--bg);
  }
  .watch-space {
    position: absolute;
    left: var(--watch-left);
    top: var(--watch-top);
    width: var(--watch-width);
    height: var(--watch-height);
    transform-origin: 0 0;
    /* Establish a containing block for fixed chat/dock children in both modes. */
    transform: translateZ(0);
    container: watch / size;
    overflow: hidden;
    --safe-t: env(safe-area-inset-top, 0px);
    --safe-r: env(safe-area-inset-right, 0px);
    --safe-b: env(safe-area-inset-bottom, 0px);
    --safe-l: env(safe-area-inset-left, 0px);
  }
  .watch-space[data-sideways='true'] {
    transform: translateX(var(--watch-physical-width)) rotate(90deg);
    --safe-t: env(safe-area-inset-right, 0px);
    --safe-r: env(safe-area-inset-bottom, 0px);
    --safe-b: env(safe-area-inset-left, 0px);
    --safe-l: env(safe-area-inset-top, 0px);
  }
</style>
